import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Clock, CheckCircle, AlertTriangle, BookOpen, Edit3, MessageSquare, Youtube, FileText, Link, Bot, Maximize2, Users, GraduationCap, Lock } from 'lucide-react';
import apiClient from '../services/apiClient';
import B4CoachPanel from '../components/B4CoachPanel';
import B6PeerReviewPanel from '../components/B6PeerReviewPanel';
import B2EditLockManager from '../components/B2EditLockManager';
import { useAuth } from '../context/AuthContext';

/**
 * StudentWeekExecutionPage - Sprint 1 Week 2
 * 
 * Vista de Ejecución Semanal - Capa 3 del Student Execution System
 * Propósito: **Máquina del curso donde ocurre el trabajo real**
 * 
 * Sprint 1 Implementation:
 * - B1 (referencia) ✅
 * - B2 (formulario grupal) ✅  
 * - B5 (declaración) ✅
 * - Comentarios individuales ✅
 * - Autosave cada 5 segundos ✅
 * - Motor de habilitación B1-B6 ✅
 * 
 * IMPORTANTE: Esta página se usa CON DashboardLayout (no incluido aquí)
 * Route: /course/:sectionId/week/:weekNumber
 */

const StudentWeekExecutionPage = () => {
  const { sectionId, weekNumber } = useParams();
  const navigate = useNavigate();
  const weekNum = parseInt(weekNumber, 10);
  const { user: authUser } = useAuth();
  
  // Estados principales
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  
  // Datos del curso
  const [courseDefinition, setCourseDefinition] = useState(null);
  const [weekData, setWeekData] = useState(null);
  const [myGroup, setMyGroup] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [comments, setComments] = useState([]);
  
  // Estados del formulario
  const [b2Responses, setB2Responses] = useState({});
  const [b5Declaration, setB5Declaration] = useState('');
  const [b5DraftSaving, setB5DraftSaving] = useState(false);
  const [b5DraftLastSaved, setB5DraftLastSaved] = useState(null);
  const [b5Confirmations, setB5Confirmations] = useState([]);
  const [b5Confirming, setB5Confirming] = useState(false);
  const [groupMembers, setGroupMembers] = useState([]);
  const [b7Review, setB7Review] = useState(null);
  const [myComment, setMyComment] = useState('');
  const [confirmingComment, setConfirmingComment] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  
  // Estados de habilitación (Motor B1-B6)
  const [blockAvailability, setBlockAvailability] = useState({
    b1: true,  // Siempre visible
    b2: false,
    b3: true,  // Siempre como referencia
    b4: false, // Sprint 3
    b5: false,
    b6: false,  // Sprint 4
    b7: false   // Comentario docente
  });

  // Paso activo del wizard de navegación lateral
  const [activeStep, setActiveStep] = useState('b1');
  // ¿Ha usado el Coach IA al menos una vez esta semana?
  const [b4HasInteraction, setB4HasInteraction] = useState(false);
  // Recursos B1 marcados como vistos por el estudiante (estado local, no persiste)
  const [viewedResources, setViewedResources] = useState({});
  const toggleResourceViewed = (resourceId) =>
    setViewedResources(prev => ({ ...prev, [resourceId]: !prev[resourceId] }));

  // Salvaguarda: si activeStep es 'b4' pero B4 no está habilitado → volver a 'b1'
  useEffect(() => {
    if (activeStep === 'b4' && !blockAvailability.b4) {
      setActiveStep('b1');
    }
  }, [activeStep, blockAvailability.b4]);

  useEffect(() => {
    loadWeekData();
  }, [sectionId, weekNum]);

  // Ref siempre actual de b2Responses — se sincroniza síncronamente en handleB2Change
  // Esto evita el stale-closure de React 18 cuando el usuario escribe y hace clic rápido
  const b2ResponsesRef = useRef({});

  // Ref para el draft de B5 (mismo patrón que b2ResponsesRef)
  const b5DraftRef = useRef('');

  // Ref para flag de guardado en progreso (B2)
  const isSavingRef = useRef(false);
  // Ref para flag de guardado en progreso (B5)
  const isSavingB5Ref = useRef(false);

  // Refs estables para los intervals de autosave
  const autoSaveWorkRef = useRef(null);
  const autoSaveB5Ref   = useRef(null);
  useEffect(() => {
    autoSaveWorkRef.current = autoSaveWork;
  });
  useEffect(() => {
    autoSaveB5Ref.current = autoSaveB5Draft;
  });

  // Autosave cada 5 segundos — interval fijo para B2 y B5
  useEffect(() => {
    const interval = setInterval(() => {
      autoSaveWorkRef.current?.();
      autoSaveB5Ref.current?.();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadWeekData = async () => {
    try {
      setLoading(true);
      
      // 1. Obtener grupo del estudiante filtrando por sectionId
      const groupsResponse = await apiClient.get('/student-execution/my-groups');
      const allGroups = groupsResponse.data.data || [];
      
      // Filtrar grupos por sectionId (arquitectura correcta)
      const sectionGroups = allGroups.filter(group => {
        const groupSectionId = typeof group.sectionId === 'string'
          ? group.sectionId
          : group.sectionId?.sectionId;
        return groupSectionId === sectionId;
      });
      
      if (sectionGroups.length === 0) {
        setError('No estás asignado a ningún grupo en esta sección');
        return;
      }
      
      const group = sectionGroups[0];
      setMyGroup(group);
      
      // 2. Obtener submission grupal + weekTemplate + comentarios (todo en un endpoint)
      // Backend devuelve: { submission, comments, weekTemplate, groupMembers }
      const submissionResponse = await apiClient.get(`/student-execution/groups/${group.groupId}/week/${weekNum}`);
      const responseData = submissionResponse.data.data;
      
      if (!responseData) {
        setError('Error al obtener datos de la semana');
        return;
      }
      
      const submissionData = responseData.submission;
      const weekComments = responseData.comments || [];
      const weekTemplate = responseData.weekTemplate;
      
      if (!weekTemplate) {
        setError(`La semana ${weekNum} no está configurada en este curso`);
        return;
      }
      
      // Establecer datos
      setSubmission(submissionData);
      setComments(weekComments);
      setWeekData(weekTemplate); // weekTemplate viene del backend con estructura completa
      setCourseDefinition({ weeks: [weekTemplate] }); // Para compatibilidad con código existente
      setB5Confirmations(responseData.b5Confirmations || []);
      setGroupMembers(responseData.groupMembers || []);
      setB7Review(responseData.b7Review || null);
      
      // 3. Cargar respuestas B2 existentes
      if (submissionData?.content?.b2_responses) {
        setB2Responses(submissionData.content.b2_responses);
        b2ResponsesRef.current = submissionData.content.b2_responses; // Sincronizar ref
      }
      
      // 4. Cargar declaración B5 o borrador (deserializando a objeto si aplica)
      if (submissionData?.content?.b5_declaration?.summary) {
        const raw = submissionData.content.b5_declaration.summary;
        b5DraftRef.current = raw;
        setB5Declaration(parseB5Draft(raw));
      } else if (submissionData?.content?.b5_draft?.summary) {
        const raw = submissionData.content.b5_draft.summary;
        b5DraftRef.current = raw;
        setB5Declaration(parseB5Draft(raw));
      }
      
      // 5. Encontrar mi comentario existente
      const myUserId = getCurrentUserId();
      const myExistingComment = weekComments.find(comment => comment.userId?.toString() === myUserId?.toString());
      if (myExistingComment) {
        setMyComment(myExistingComment.comment);
      }
      
      // 6. Calcular disponibilidad de bloques
      updateBlockAvailability(submissionData, weekComments, group, weekTemplate);
      
    } catch (error) {
      console.error('Error loading week data:', error);
      setError('Error al cargar datos de la semana');
    } finally {
      setLoading(false);
    }
  };

  const updateBlockAvailability = (submission, comments, group, weekTemplate) => {
    const hasB2Content = submission.content?.b2_responses && Object.keys(submission.content.b2_responses).length > 0;
    const activeMembers = group?.members?.length || 0;
    const allCommented = activeMembers > 0 && comments.length >= activeMembers;
    const isFinalized = submission.status === 'Finalized' || submission.status === 'Completed';
    const isEditable = !isFinalized;
    // B4 visible si el docente lo configuró como enabled en la plantilla de la semana
    const b4Enabled = weekTemplate?.b4_coach_config?.enabled !== false;
    
    setBlockAvailability({
      b1: true,
      b2: true,                              // Siempre visible, el disabled lo controla B2EditLockManager
      b3: true,
      b4: b4Enabled,
      b5: allCommented && !isFinalized,
      b5Completed: isFinalized,
      b6: false,
      b7: false
    });
  };

  const getCurrentUserId = () => {
    // Usar el contexto de auth (el user object NO se guarda en localStorage)
    return authUser?.userId || authUser?._id?.toString() || null;
  };

  const getCurrentUserName = () => {
    return authUser?.nombre || authUser?.name || 'Usuario';
  };

  // Función base de guardado — lee siempre del ref (nunca de closure)
  // No tiene b2Responses en deps → es estable y siempre guarda el dato más reciente
  const doSave = useCallback(async () => {
    const current = b2ResponsesRef.current;
    if (!submission || !myGroup || Object.keys(current).length === 0) return;
    try {
      setSaving(true);
      await apiClient.post(`/student-execution/groups/${myGroup.groupId}/week/${weekNum}/work`, {
        b2_responses: current
      });
      setLastSaved(new Date());
    } catch (error) {
      console.error('Error saving B2:', error);
    } finally {
      setSaving(false);
    }
  }, [submission, myGroup, weekNum]);

  // Autosave para el interval — con guardia anti-concurrencia
  const autoSaveWork = useCallback(async () => {
    if (isSavingRef.current) return;
    isSavingRef.current = true;
    await doSave();
    isSavingRef.current = false;
  }, [doSave]);

  // Guardado forzado para el botón "Terminar Edición" — SIEMPRE guarda
  // Si hay un autosave en progreso, espera que termine y luego guarda los datos más recientes
  const forceSaveWork = useCallback(async () => {
    // Esperar autosave en curso (máx 3s)
    let waited = 0;
    while (isSavingRef.current && waited < 30) {
      await new Promise(r => setTimeout(r, 100));
      waited++;
    }
    isSavingRef.current = true;
    await doSave();
    isSavingRef.current = false;
  }, [doSave]);

  // Refresca solo las respuestas B2 sin recargar toda la página
  // Se dispara automáticamente cuando un compañero termina de editar
  const refreshB2Content = useCallback(async () => {
    if (!myGroup) return;
    try {
      // _t evita caché del navegador al refrescar contenido B2 de otro usuario
      const res = await apiClient.get(`/student-execution/groups/${myGroup.groupId}/week/${weekNum}`, { params: { _t: Date.now() } });
      const data = res.data.data;
      if (data?.submission?.content?.b2_responses) {
        b2ResponsesRef.current = data.submission.content.b2_responses; // Sincronizar ref
        setB2Responses(data.submission.content.b2_responses);
      }
    } catch (_e) {
      // Silencioso — no interrumpir la UX
    }
  }, [myGroup, weekNum]);

  // Deserializa un string guardado en b5-draft a objeto de secciones
  // Backward-compatible: si es texto plano, lo pone en clave '_legacy'
  const parseB5Draft = (raw) => {
    if (!raw) return {};
    if (typeof raw === 'string' && raw.startsWith('{')) {
      try { return JSON.parse(raw); } catch (_) { /* fallthrough */ }
    }
    return { _legacy: raw };
  };

  // Serializa el objeto de secciones a string para guardar en PATCH /b5-draft
  const serializeB5Draft = (obj) => {
    if (!obj || Object.keys(obj).length === 0) return '';
    return JSON.stringify(obj);
  };

  // Refresca el borrador B5 del servidor — se dispara cuando un compañero termina de editar B5
  const refreshB5Content = useCallback(async () => {
    if (!myGroup) return;
    try {
      const res = await apiClient.get(`/student-execution/groups/${myGroup.groupId}/week/${weekNum}`, { params: { _t: Date.now() } });
      const data = res.data.data;
      const draft = data?.submission?.content?.b5_draft;
      if (draft?.summary !== undefined) {
        const parsed = parseB5Draft(draft.summary);
        b5DraftRef.current = draft.summary;
        setB5Declaration(parsed);
      }
    } catch (_e) {
      // Silencioso
    }
  }, [myGroup, weekNum]);

  // Guardado base de B5 draft — lee siempre del ref
  const doSaveB5Draft = useCallback(async () => {
    const current = b5DraftRef.current;
    if (!myGroup) return;
    try {
      setB5DraftSaving(true);
      await apiClient.patch(`/student-execution/groups/${myGroup.groupId}/week/${weekNum}/b5-draft`, {
        summary: current
      });
      setB5DraftLastSaved(new Date());
    } catch (_e) {
      // Silencioso — no interrumpir la UX
    } finally {
      setB5DraftSaving(false);
    }
  }, [myGroup, weekNum]);

  // Autosave B5 con guardia anti-concurrencia y guardia de semana finalizada
  const autoSaveB5Draft = useCallback(async () => {
    // No guardar si la semana ya fue declarada (evita el 400)
    if (blockAvailability.b5Completed) return;
    if (isSavingB5Ref.current) return;
    isSavingB5Ref.current = true;
    await doSaveB5Draft();
    isSavingB5Ref.current = false;
  }, [doSaveB5Draft, blockAvailability.b5Completed]);

  // Guardado forzado B5 — llamado por "Terminar Edición" en el lock manager
  const forceB5Save = useCallback(async () => {
    let waited = 0;
    while (isSavingB5Ref.current && waited < 30) {
      await new Promise(r => setTimeout(r, 100));
      waited++;
    }
    isSavingB5Ref.current = true;
    await doSaveB5Draft();
    isSavingB5Ref.current = false;
  }, [doSaveB5Draft]);

  const handleB2Change = (fieldId, value) => {
    // 1. Actualizar ref síncronamente EN EL EVENT HANDLER (no dentro del updater).
    //    El event handler corre de forma síncrona. Esto garantiza que doSave/forceSaveWork
    //    siempre lean el último carácter escrito, incluso si el usuario hace clic en
    //    "Terminar" antes de que React haya procesado el re-render.
    b2ResponsesRef.current = { ...b2ResponsesRef.current, [fieldId]: value };
    // 2. Actualizar estado React con functional updater — correcto para React 18 batching.
    //    No asignamos ref dentro del updater (evita side-effect en fase de render).
    setB2Responses(prev => ({ ...prev, [fieldId]: value }));
  };

  // value puede ser string (legacy) u objeto { sectionId: texto }
  const handleB5DraftChange = (value) => {
    const serialized = typeof value === 'string' ? value : serializeB5Draft(value);
    b5DraftRef.current = serialized;
    setB5Declaration(typeof value === 'string' ? parseB5Draft(value) : value);
  };

  // Paso 1: clic en "Enviar comentario" → muestra confirmación
  const handleRequestConfirmComment = () => {
    if (!myComment.trim() || myComment.length < 50) {
      alert('El comentario debe tener al menos 50 caracteres');
      return;
    }
    setConfirmingComment(true);
  };

  // Paso 2: confirma → envía al backend, queda permanente
  const handleConfirmComment = async () => {
    setSubmittingComment(true);
    try {
      await apiClient.post(`/student-execution/groups/${myGroup.groupId}/week/${weekNum}/comment`, {
        comment: myComment
      });
      setConfirmingComment(false);
      // Actualización optimista inmediata: agrega el comentario al estado local
      // para que myExistingComment lo encuentre SIN depender de la comparación de userId
      const myId = getCurrentUserId();
      setComments(prev => {
        // Si por alguna razón ya está (rara vez), no duplicar
        const alreadyIn = prev.some(c => c.userId?.toString() === myId?.toString());
        if (alreadyIn) return prev;
        return [...prev, {
          userId: myId,
          comment: myComment,
          userName: getCurrentUserName(),
          requirements: { wordCount: myComment.trim().split(/\s+/).length, meetsRequirement: true },
          createdAt: new Date().toISOString()
        }];
      });
      // Luego sincroniza con el servidor (para contar bien, actualizar blockAvailability, etc.)
      await loadWeekData();
    } catch (error) {
      console.error('Error saving comment:', error);
      setConfirmingComment(false);
      if (error.response?.status === 409) {
        // Ya existe comentario: recargar para mostrar el estado correcto
        await loadWeekData();
      } else if (error.response?.data?.code === 'CONTENT_FLAGGED') {
        alert(error.response.data.message || 'Tu comentario contiene lenguaje inapropiado. Corrígelo antes de enviar.');
      } else {
        alert('Error al guardar comentario');
      }
    } finally {
      setSubmittingComment(false);
    }
  };

  // Devuelve el texto plano total de b5Declaration (string o objeto de secciones)
  const getB5Text = () => {
    if (!b5Declaration) return '';
    if (typeof b5Declaration === 'string') return b5Declaration.trim();
    return Object.values(b5Declaration).join(' ').trim();
  };

  const handleConfirmB5 = async () => {
    if (!getB5Text() || getB5Text().length < 50) {
      alert('El resumen debe tener al menos 50 caracteres para confirmar');
      return;
    }
    if (!blockAvailability.b5) {
      alert('No se puede confirmar hasta que todos los miembros hayan comentado');
      return;
    }
    try {
      setB5Confirming(true);
      const result = await apiClient.post(
        `/student-execution/groups/${myGroup.groupId}/week/${weekNum}/confirm-b5`
      );
      const { allConfirmed, confirmations } = result.data.data;
      // Actualizar confirmaciones inmediatamente con lo que devuelve el servidor
      if (confirmations) {
        setB5Confirmations(confirmations);
      }
      if (allConfirmed) {
        alert('¡Todos confirmaron! La semana quedó declarada completada.');
        await loadWeekData();
      } else {
        alert('Tu confirmación fue registrada. Esperando al resto del grupo.');
      }
    } catch (error) {
      console.error('Error confirming B5:', error);
      alert(error.response?.data?.message || 'Error al confirmar declaración');
    } finally {
      setB5Confirming(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center">
          <AlertTriangle className="w-5 h-5 text-red-500 mr-2" />
          <h3 className="text-red-800 font-medium">Error</h3>
        </div>
        <p className="text-red-700 mt-2">{error}</p>
        <button
          onClick={() => navigate(`/course/${sectionId}/workspace`)}
          className="mt-4 text-red-600 hover:text-red-800 font-medium"
        >
          Volver al Workspace
        </button>
      </div>
    );
  }

  if (!weekData || !myGroup || !submission) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-gray-900 font-medium">Datos incompletos</h3>
        <p className="text-gray-600 mt-1">No se pudo cargar la información de la semana</p>
      </div>
    );
  }

  const myUserId = getCurrentUserId();
  const myExistingComment = comments.find(c => c.userId?.toString() === myUserId?.toString());
  const activeMembers = myGroup.members.filter(m => m.status === 'active');
  const commentsCount = comments.length;
  const requiredComments = activeMembers.length;

  // --- Helpers para el sidebar de actividades ---
  // Regla estricta: al menos un campo con contenido real (no key vacía)
  const hasB2Content = Object.values(b2Responses || {}).some(v => String(v || '').trim() !== '');

  // ── Checkpoints de progreso semanal ──────────────────────────────
  // B2: todos los campos required tienen contenido
  // Si el template no define campos required, basta con que haya cualquier contenido
  const b2RequiredFields = weekData?.b2_execution_template?.fields?.filter(f => f.required) || [];
  const b2RequiredComplete = b2RequiredFields.length > 0
    ? b2RequiredFields.every(f => String(b2Responses?.[f.fieldId] || '').trim() !== '')
    : hasB2Content;

  // B4: al menos 1 interacción con el coach (ya calculado: b4HasInteraction)

  // B5.A: todos los miembros del grupo han dejado su comentario
  const b5AComplete = commentsCount >= requiredComments && requiredComments > 0;

  // B5.B: todos confirmaron la declaración (ya en blockAvailability.b5Completed)

  // Progreso total sobre 4 checkpoints
  const checkpoints = [
    { id: 'b2', label: 'Producción', done: b2RequiredComplete },
    { id: 'b4', label: 'Mejora',     done: b4HasInteraction },
    { id: 'b5a', label: 'Reflexión', done: b5AComplete },
    { id: 'b5b', label: 'Declaración', done: blockAvailability.b5Completed },
  ];
  const checkpointsDone = checkpoints.filter(c => c.done).length;
  const progressPct = Math.round((checkpointsDone / checkpoints.length) * 100);

  /** Extrae el videoId de una URL de YouTube */
  const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
    return match ? match[1] : null;
  };

  const steps = [
    {
      id: 'b1',
      label: 'Recursos',
      icon: <BookOpen className="w-4 h-4" />,
      completed: false,
      available: blockAvailability.b1,
    },
    {
      id: 'b2',
      label: 'Producción',
      icon: <Edit3 className="w-4 h-4" />,
      completed: b2RequiredComplete,
      available: blockAvailability.b2,
    },
    {
      id: 'b3',
      label: 'Criterios',
      icon: <CheckCircle className="w-4 h-4" />,
      completed: false,
      available: blockAvailability.b3,
    },
    {
      id: 'b4',
      label: 'Mejora',
      icon: <Bot className="w-4 h-4" />,
      completed: b4HasInteraction,
      available: blockAvailability.b4,
    },
    {
      id: 'b5',
      label: 'Declaración',
      icon: <MessageSquare className="w-4 h-4" />,
      completed: blockAvailability.b5Completed,
      available: true,
    },
    {
      id: 'b6',
      label: 'Revisión',
      icon: <Users className="w-4 h-4" />,
      completed: false,
      available: false,
    },
    {
      id: 'b7',
      label: 'Evaluación',
      icon: <GraduationCap className="w-4 h-4" />,
      completed: false,
      available: false,
    },
  ];

  return (
    <div className="flex flex-col min-h-full">
      {/* ── Header compacto sticky + Tabs mobile ─────────────────── */}
      <div className="sticky top-0 z-20">
        {/* Header de contexto de semana */}
        <div className="bg-white border-b border-gray-200 px-4 md:px-6 py-3">
          {/* Breadcrumb: Programas / {curso} / Semana N */}
          <nav className="flex items-center gap-1.5 text-sm mb-2">
            <button
              onClick={() => navigate('/cursos-grupales')}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              Programas
            </button>
            <span className="text-gray-300">/</span>
            <button
              onClick={() => navigate(`/course/${sectionId}/workspace`)}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              {(myGroup?.courseInfo?.title || myGroup?.sectionInfo?.name || 'Curso').replace(/^Programa\s+/i, '')}
            </button>
            <span className="text-gray-300">/</span>
            <span className="text-gray-900 font-medium">Semana {weekNum}</span>
          </nav>
          <h1 className="text-base md:text-lg font-semibold text-gray-900 leading-tight">
            Semana {weekNum} · {weekData.title}
          </h1>
          <p className="text-xs md:text-sm text-gray-500 mt-0.5">{Array.isArray(weekData.learningObjectives) ? weekData.learningObjectives.join(' · ') : weekData.learningObjectives}</p>
        </div>

        {/* Tabs mobile (solo < md) — barra compacta de íconos */}
        <div className="flex md:hidden bg-white border-b border-gray-200 shrink-0">
          {/* 7 íconos repartidos en todo el ancho */}
          <div className="flex flex-1">
            {steps.filter(s => s.id !== 'b4' || blockAvailability.b4).map(step => {
              const isActive = activeStep === step.id;
              const isActionable = ['b2', 'b4', 'b5'].includes(step.id);
              const isCompleted = step.completed;
              return (
                <button
                  key={step.id}
                  onClick={() => setActiveStep(step.id)}
                  className="relative flex flex-col items-center justify-center py-2 flex-1"
                  title={step.label}
                >
                  <span className={`${
                    isActive
                      ? 'text-blue-600'
                      : isActionable && isCompleted
                      ? 'text-green-600'
                      : isActionable
                      ? 'text-gray-600'
                      : 'text-gray-300'
                  }`}>
                    {step.icon}
                  </span>
                  {/* Punto de estado bajo el ícono */}
                  <span className={`mt-0.5 w-1 h-1 rounded-full ${
                    isActionable && isCompleted
                      ? 'bg-green-500'
                      : isActionable
                      ? 'bg-orange-400'
                      : 'bg-transparent'
                  }`} />
                  {/* Línea activa */}
                  {isActive && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Cuerpo: sidebar desktop + área de contenido ─────────────── */}
      <div className="flex flex-1">

        {/* Sidebar desktop (oculto en mobile) */}
        <aside className="hidden md:flex flex-col w-60 shrink-0 border-r border-gray-200 bg-white sticky top-20 self-start">
          <div className="p-4 space-y-1">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Actividades
              </p>
              <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${
                checkpointsDone === checkpoints.length
                  ? 'bg-green-100 text-green-700'
                  : 'bg-blue-50 text-blue-600'
              }`}>
                {checkpointsDone}/{checkpoints.length}
              </span>
            </div>

            {steps.filter(s => s.id !== 'b4' || blockAvailability.b4).map(step => {
              const isActive = activeStep === step.id;
              // Bloques donde el alumno tiene acción pendiente o completada
              const isActionable = ['b2', 'b4', 'b5'].includes(step.id);
              const isCompleted = step.completed;
              return (
                <button
                  key={step.id}
                  onClick={() => setActiveStep(step.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left ${
                    isActive
                      ? 'bg-blue-600 text-white'
                      : isActionable && isCompleted
                      ? 'text-green-700 hover:bg-green-50'
                      : isActionable && !isCompleted
                      ? 'text-gray-700 hover:bg-gray-100'
                      : 'text-gray-400 hover:bg-gray-50' // informativo: B1, B3, B6, B7
                  }`}
                >
                  {step.icon}
                  <span className="flex-1">{step.label}</span>
                  {/* Indicador solo en bloques accionables */}
                  {isActionable && (
                    <span className={`w-2 h-2 rounded-full shrink-0 transition-colors ${
                      isCompleted ? 'bg-green-500' : 'bg-orange-400'
                    }`} />
                  )}
                </button>
              );
            })}
          </div>
        </aside>

        {/* Área de contenido activo */}
        <div className="flex-1 min-w-0 p-4 md:p-6">

          {/* ── B1: Recursos ──────────────────────────────────── */}
          {activeStep === 'b1' && (
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4 hidden md:flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-blue-600" />
                Recursos de Referencia
              </h2>
              {(!weekData.b1_resources || weekData.b1_resources.length === 0) ? (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                  <BookOpen className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">No hay recursos configurados para esta semana.</p>
                </div>
              ) : (
                // Orden = orden pedagógico definido por el docente (array order = teacher order)
                <div className="space-y-5">
                  {weekData.b1_resources.map((resource, index) => {
                    const url = resource.url;
                    const youtubeId = url ? getYouTubeId(url) : null;
                    // Ícono de tipo: comunica el formato antes de que el estudiante interactúe
                    const TypeIcon = youtubeId
                      ? <Youtube className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                      : resource.type === 'pdf'
                      ? <FileText className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                      : <Link className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />;
                    return (
                      <div key={index} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                        {/* Cabecera: Línea 1 (título + expand) + Línea 2 (meta + acciones) */}
                        <div className="px-4 pt-4 pb-2">
                          {/* Línea 1: icono tipo + título + maximizar */}
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-2 min-w-0">
                              {TypeIcon}
                              <h3 className="font-medium text-gray-900 text-sm leading-tight">{resource.title}</h3>
                            </div>
                            {url && (youtubeId || resource.type === 'pdf') && (
                              <a
                                href={youtubeId ? `https://www.youtube.com/watch?v=${youtubeId}` : url}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Abrir en nueva pestaña"
                                className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors shrink-0"
                              >
                                <Maximize2 className="w-3.5 h-3.5" />
                              </a>
                            )}
                          </div>
                          {/* Línea 2: duración + badges/acciones */}
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            {resource.estimatedMinutes > 0 && (
                              <span className="text-xs text-gray-400 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {resource.estimatedMinutes} min
                              </span>
                            )}
                            {resource.mandatory && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                Obligatorio
                              </span>
                            )}
                            <button
                              onClick={() => toggleResourceViewed(resource.resourceId || index)}
                              title={viewedResources[resource.resourceId || index] ? 'Marcar como no visto' : 'Marcar como visto'}
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border transition-colors ${
                                viewedResources[resource.resourceId || index]
                                  ? 'bg-green-100 text-green-700 border-green-200'
                                  : 'bg-white text-gray-400 border-gray-200 hover:border-green-300 hover:text-green-600'
                              }`}
                            >
                              <CheckCircle className="w-3 h-3" />
                              {viewedResources[resource.resourceId || index] ? 'Visto' : 'Marcar visto'}
                            </button>
                          </div>
                        </div>
                        {/* Embed / CTA — flujo directo bajo el título */}
                        {url && (
                          <div className="px-2 md:px-4 pb-3 md:pb-4">
                            {youtubeId ? (
                              <div className="aspect-video w-full rounded-sm md:rounded overflow-hidden bg-black">
                                <iframe
                                  src={`https://www.youtube.com/embed/${youtubeId}`}
                                  className="w-full h-full"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                  title={resource.title}
                                />
                              </div>
                            ) : resource.type === 'pdf' ? (
                              <div>
                                <iframe
                                  src={url}
                                  className="w-full h-64 md:h-96 rounded-sm md:rounded border border-gray-200"
                                  title={resource.title}
                                />
                                <a
                                  href={url}
                                  download
                                  className="mt-2 inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                                >
                                  <FileText className="w-4 h-4" />
                                  Descargar PDF
                                </a>
                              </div>
                            ) : (
                              <div className="flex items-center justify-between gap-4 bg-gray-50 rounded-lg p-3">
                                <p className="text-xs text-gray-500">Este recurso se abrirá en una nueva pestaña</p>
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="shrink-0 px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                                >
                                  Abrir recurso
                                </a>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── B2: Trabajo Grupal ────────────────────────────── */}
          {activeStep === 'b2' && (
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4 hidden md:flex items-center gap-2">
                <Edit3 className="w-5 h-5 text-blue-600" />
                Producción
              </h2>
              {weekData.b2_execution_template?.fields ? (
                <>
                  {weekData.b2_execution_template.instructions && (
                    <p className="text-gray-600 mb-4 text-sm">{weekData.b2_execution_template.instructions}</p>
                  )}
                  <B2EditLockManager
                    submissionId={submission?.submissionId}
                    groupId={myGroup?.groupId}
                    weekNumber={weekNum}
                    currentUserId={getCurrentUserId()}
                    currentUserName={JSON.parse(localStorage.getItem('user') || '{}').nombre || 'Estudiante'}
                    onSave={forceSaveWork}
                    onContentUpdate={refreshB2Content}
                    lastSaved={lastSaved}
                    saving={saving}
                    disabled={submission?.status === 'Completed' || submission?.status === 'Finalized'}
                  >
                    <div className="space-y-4">
                      {weekData.b2_execution_template.fields.map((field) => {
                        const fieldId = field.fieldId;
                        return (
                          <div key={fieldId}>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {field.label}
                              {field.required && <span className="text-red-500 ml-1">*</span>}
                            </label>
                            {field.type === 'textarea' ? (
                              <textarea
                                value={b2Responses[fieldId] || ''}
                                onChange={(e) => handleB2Change(fieldId, e.target.value)}
                                placeholder={field.placeholder || ''}
                                rows={4}
                                disabled={blockAvailability.b5Completed}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed"
                              />
                            ) : field.type === 'select' ? (
                              <select
                                value={b2Responses[fieldId] || ''}
                                onChange={(e) => handleB2Change(fieldId, e.target.value)}
                                disabled={blockAvailability.b5Completed}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed"
                              >
                                <option value="">Seleccionar...</option>
                                {field.options && field.options.map((option, idx) => (
                                  <option key={idx} value={option}>{option}</option>
                                ))}
                              </select>
                            ) : (
                              <input
                                type={field.type || 'text'}
                                value={b2Responses[fieldId] || ''}
                                onChange={(e) => handleB2Change(fieldId, e.target.value)}
                                placeholder={field.placeholder || ''}
                                disabled={blockAvailability.b5Completed}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed"
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </B2EditLockManager>
                </>
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                  <p className="text-gray-500 text-sm">No hay formulario configurado para esta semana.</p>
                </div>
              )}
            </div>
          )}

          {/* ── B3: Criterios de Evaluación ──────────────────── */}
          {activeStep === 'b3' && (
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4 hidden md:flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-blue-600" />
                Criterios de Evaluación
              </h2>
              {(!weekData.b3_criteria || weekData.b3_criteria.length === 0) ? (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                  <p className="text-gray-500 text-sm">No hay criterios configurados para esta semana.</p>
                </div>
              ) : (
                <>
                  <p className="text-gray-600 mb-4 text-sm">Estos criterios serán utilizados para evaluar tu trabajo:</p>
                  <div className="space-y-3">
                    {weekData.b3_criteria.map((criterion, index) => (
                      <div key={index} className="bg-white rounded-lg border border-gray-200 p-4 flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-xs font-semibold text-blue-700">
                          {index + 1}
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900 text-sm">{criterion.name}</h4>
                          <p className="text-sm text-gray-600 mt-0.5">{criterion.description}</p>
                          {criterion.weight && (
                            <span className="text-xs text-gray-400 mt-1 inline-block">Peso: {criterion.weight}%</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── B4: Coach IA ─────────────────────────────────── */}
          {activeStep === 'b4' && blockAvailability.b4 && (
            <div>
              <B4CoachPanel
                groupId={myGroup?.groupId}
                weekNumber={weekNum}
                submissionId={submission?.submissionId}
                isEnabled={blockAvailability.b4}
                weekFinalized={blockAvailability.b5Completed}
                hasB2Content={Object.values(b2Responses || {}).some(v => String(v || '').trim() !== '')}
                onStatusChange={(hasIt) => setB4HasInteraction(hasIt)}
              />
            </div>
          )}

          {/* ── B5: Reflexión Individual + Formalización ─────── */}
          {activeStep === 'b5' && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900 hidden md:flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-blue-600" />
                Declaración
                <span className="text-sm font-normal text-gray-500">
                  ({commentsCount}/{requiredComments})
                </span>
              </h2>

              {/* Mi comentario */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tu comentario personal sobre el trabajo de esta semana:
                </label>

                {blockAvailability.b5Completed && !myExistingComment && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <p className="text-sm text-gray-500">La semana fue declarada completada. Ya no es posible agregar comentarios.</p>
                  </div>
                )}

                {!myExistingComment && !blockAvailability.b5Completed && (
                  <>
                    <textarea
                      value={myComment}
                      onChange={(e) => { if (!confirmingComment) setMyComment(e.target.value); }}
                      placeholder="Describe tu participación, aprendizajes, dificultades encontradas, etc. (mínimo 50 caracteres)"
                      rows={4}
                      disabled={confirmingComment}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                    />
                    <div className="flex items-center justify-between mt-2">
                      <span className={`text-sm ${myComment.length < 50 ? 'text-red-500' : 'text-green-500'}`}>
                        {myComment.length}/50 caracteres mínimos
                      </span>
                      {!confirmingComment && (
                        <button
                          onClick={handleRequestConfirmComment}
                          disabled={myComment.length < 50}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium"
                        >
                          Enviar comentario
                        </button>
                      )}
                    </div>
                    {confirmingComment && (
                      <div className="mt-3 bg-amber-50 border border-amber-300 rounded-lg p-4">
                        <p className="text-sm font-medium text-amber-800 mb-1">¿Confirmas el envío?</p>
                        <p className="text-xs text-amber-700 mb-3">
                          Una vez enviado, el comentario quedará registrado de forma permanente y no podrá modificarse ni eliminarse.
                        </p>
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => setConfirmingComment(false)}
                            disabled={submittingComment}
                            className="px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                          >
                            Cancelar
                          </button>
                          <button
                            onClick={handleConfirmComment}
                            disabled={submittingComment}
                            className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
                          >
                            {submittingComment ? 'Enviando...' : 'Sí, enviar comentario'}
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {myExistingComment && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-xs font-medium text-green-700">Comentario enviado</span>
                    </div>
                    <p className="text-gray-800 whitespace-pre-wrap text-sm">{myExistingComment.comment}</p>
                    <p className="text-xs text-gray-400 mt-2">{myExistingComment.requirements?.wordCount ?? ''} palabras</p>
                  </div>
                )}
              </div>

              {/* Comentarios del grupo */}
              <div>
                <h3 className="font-medium text-gray-900 mb-3 text-sm">Comentarios del grupo:</h3>
                {comments.length === 0 ? (
                  <p className="text-sm text-gray-400">Aún no hay comentarios.</p>
                ) : (
                  <div className="space-y-3">
                    {comments.map((comment) => (
                      <div key={comment.commentId} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-gray-900 text-sm">{comment.userName || 'Miembro del grupo'}</span>
                          <span className="text-xs text-gray-500">{new Date(comment.createdAt || comment.timestamp).toLocaleString()}</span>
                        </div>
                        <p className="text-gray-700 text-sm">{comment.comment}</p>
                        <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                          <span>{comment.requirements?.wordCount || comment.wordCount || 0} palabras</span>
                          {(comment.requirements?.meetsRequirement || comment.meetsRequirement) && (
                            <span className="text-green-600 flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />
                              Cumple requisitos
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

              </div>

              {/* B5 completado — solo lectura */}
              {blockAvailability.b5Completed && (
                <div className="bg-green-50 border border-green-300 rounded-lg p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-500 text-white text-xs font-bold">B5</span>
                    <h3 className="font-semibold text-gray-900">Formalización</h3>
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <p className="text-green-700 font-medium text-sm mb-2">Semana declarada como completada</p>
                  {submission?.content?.b5_declaration?.summary && (
                    <div className="bg-white rounded-lg p-4 border border-green-200">
                      <p className="text-xs text-gray-500 mb-1">Resumen declarado:</p>
                      <p className="text-gray-800 text-sm whitespace-pre-wrap">{submission.content.b5_declaration.summary}</p>
                    </div>
                  )}
                </div>
              )}

              {/* B5 Formalización — locked preview o activo según si todos comentaron */}
              {!blockAvailability.b5Completed && (
                <div className={`rounded-xl border-2 p-5 transition-all ${
                  blockAvailability.b5 ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'
                }`}>

                  {/* Header */}
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                      blockAvailability.b5 ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-500'
                    }`}>B5</span>
                    <h3 className="font-semibold text-gray-900">Formalización del Equipo</h3>
                    {!blockAvailability.b5 && <Lock className="w-4 h-4 text-gray-400" />}
                  </div>

                  {/* Banner de estado */}
                  {!blockAvailability.b5 ? (
                    <div className="mb-4 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                      <Clock className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-amber-800">
                          {requiredComments - commentsCount} miembro{requiredComments - commentsCount !== 1 ? 's' : ''} aún no {requiredComments - commentsCount !== 1 ? 'han' : 'ha'} completado su reflexión
                        </p>
                        <p className="text-xs text-amber-600 mt-0.5">
                          Esta sección se habilitará cuando todos los miembros hayan enviado su comentario individual.
                        </p>
                      </div>
                      {/* Puntos de progreso por miembro */}
                      <div className="flex gap-1.5 items-center mt-1 shrink-0">
                        {groupMembers.map((m, i) => {
                          const hasCmt = comments.some(c => c.userId?.toString() === m.userId?.toString());
                          return (
                            <div
                              key={m.userId || i}
                              title={`${m.userName}: ${hasCmt ? 'comentó' : 'pendiente'}`}
                              className={`w-3 h-3 rounded-full border-2 ${
                                hasCmt ? 'bg-green-500 border-green-600' : 'bg-white border-gray-300'
                              }`}
                            />
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <p className="text-green-700 mb-4 text-sm font-medium">
                      ¡Todos los miembros han comentado! Redacten el resumen del trabajo completado.
                    </p>
                  )}

                  {/* Formulario — deshabilitado visualmente si no está activo */}
                  <div className={!blockAvailability.b5 ? 'opacity-40 pointer-events-none select-none' : ''}>
                    <B2EditLockManager
                      blockType="b5"
                      submissionId={submission?.submissionId}
                      groupId={myGroup?.groupId}
                      weekNumber={weekNum}
                      currentUserId={getCurrentUserId()}
                      currentUserName={getCurrentUserName()}
                      onSave={forceB5Save}
                      onContentUpdate={refreshB5Content}
                      lastSaved={b5DraftLastSaved}
                      saving={b5DraftSaving}
                      disabled={!blockAvailability.b5}
                    >
                      {(() => {
                        const b5Template = weekData?.b5_formalization_template;
                        const sections = b5Template?.format === 'structured' && b5Template?.sections?.length > 0
                          ? b5Template.sections
                          : null;

                        if (sections) {
                          return (
                            <div className="space-y-4">
                              {sections.map((section, idx) => {
                                const sectionKey = section.id || `sec_${idx}`;
                                const currentVal = typeof b5Declaration === 'object'
                                  ? (b5Declaration[sectionKey] || '')
                                  : '';
                                return (
                                  <div key={sectionKey} className="space-y-1">
                                    <label className="block text-sm font-semibold text-gray-800">
                                      {section.title || `Sección ${idx + 1}`}
                                      {section.required && <span className="text-red-500 ml-1">*</span>}
                                    </label>
                                    {section.description && (
                                      <p className="text-xs text-gray-500 mb-1">{section.description}</p>
                                    )}
                                    <textarea
                                      value={currentVal}
                                      onChange={(e) => {
                                        const updated = typeof b5Declaration === 'object'
                                          ? { ...b5Declaration, [sectionKey]: e.target.value }
                                          : { [sectionKey]: e.target.value };
                                        handleB5DraftChange(updated);
                                      }}
                                      placeholder={`Redacten la respuesta del grupo para "${section.title || `sección ${idx + 1}`}"...`}
                                      rows={4}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          );
                        }

                        const legacyVal = typeof b5Declaration === 'object'
                          ? (b5Declaration._legacy || '')
                          : (b5Declaration || '');
                        return (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Resumen del trabajo completado:
                            </label>
                            <textarea
                              value={legacyVal}
                              onChange={(e) => handleB5DraftChange(e.target.value)}
                              placeholder="Describe brevemente lo que el grupo logró en esta semana..."
                              rows={4}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                        );
                      })()}
                    </B2EditLockManager>
                  </div>

                  {/* Panel de confirmaciones */}
                  <div className={`mt-4 bg-white rounded-lg border p-4 ${
                    !blockAvailability.b5 ? 'border-gray-200 opacity-40' : 'border-green-200'
                  }`}>
                    <p className="text-sm font-medium text-gray-700 mb-3">
                      Confirmaciones ({blockAvailability.b5 ? b5Confirmations.length : 0}/{groupMembers.length}):
                    </p>
                    <div className="space-y-2">
                      {groupMembers.map(member => {
                        const confirmed = blockAvailability.b5 && b5Confirmations.find(c => c.userId === member.userId);
                        const isMe = getCurrentUserId() === member.userId;
                        return (
                          <div key={member.userId} className="flex items-center justify-between py-1">
                            <span className="text-sm text-gray-700">
                              {member.userName}{isMe ? ' (tú)' : ''}
                            </span>
                            {confirmed ? (
                              <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
                                <CheckCircle className="w-4 h-4" /> Confirmado
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-gray-400 text-sm">
                                <Clock className="w-4 h-4" /> Pendiente
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {blockAvailability.b5 ? (
                      !b5Confirmations.find(c => c.userId === getCurrentUserId()) ? (
                        <button
                          onClick={handleConfirmB5}
                          disabled={b5Confirming || !getB5Text() || getB5Text().length < 50}
                          className="mt-4 w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium text-sm"
                        >
                          {b5Confirming ? 'Confirmando...' : 'Confirmar Declaración'}
                        </button>
                      ) : (
                        <p className="mt-3 text-sm text-green-700 text-center font-medium">
                          Ya confirmaste. Esperando al resto del grupo.
                        </p>
                      )
                    ) : (
                      <button disabled className="mt-4 w-full px-4 py-2 bg-gray-200 text-gray-400 rounded-lg cursor-not-allowed font-medium text-sm">
                        Confirmar Declaración
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeStep === 'b6' && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-900 hidden md:flex items-center gap-2">
                <Users className="w-5 h-5 text-orange-500" />
                Revisión
              </h2>
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-6 text-center">
                <Users className="w-10 h-10 text-orange-300 mx-auto mb-3" />
                <p className="text-sm font-medium text-orange-800 mb-1">Evaluación entre grupos</p>
                <p className="text-sm text-orange-600">Esta fase se activa automáticamente cuando el docente habilite la evaluación cruzada entre equipos.</p>
              </div>
            </div>
          )}

          {activeStep === 'b7' && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-900 hidden md:flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-purple-500" />
                Evaluación
              </h2>

              {!b7Review ? (
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-6 text-center">
                  <GraduationCap className="w-10 h-10 text-purple-300 mx-auto mb-3" />
                  <p className="text-sm font-medium text-purple-800 mb-1">Retroalimentación del docente</p>
                  <p className="text-sm text-purple-600">El docente aún no ha registrado la calificación para esta semana.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Score general */}
                  <div className="bg-purple-600 text-white rounded-xl px-5 py-4 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-purple-200 uppercase tracking-wider mb-0.5">Calificación ponderada</p>
                      <p className="text-3xl font-bold">
                        {b7Review.overallScore !== null ? b7Review.overallScore.toFixed(1) : '—'}
                        <span className="text-base font-normal text-purple-300 ml-1">/ 10</span>
                      </p>
                    </div>
                    {b7Review.reviewedInClass && (
                      <div className="flex items-center gap-1.5 bg-purple-500 rounded-lg px-3 py-2">
                        <CheckCircle className="w-4 h-4 text-purple-200" />
                        <span className="text-xs text-purple-100">Revisado en clase</span>
                      </div>
                    )}
                  </div>

                  {/* Criterios */}
                  {b7Review.criteria.length > 0 && (
                    <div className="bg-white border border-purple-100 rounded-xl overflow-hidden">
                      <div className="px-4 py-3 bg-purple-50 border-b border-purple-100">
                        <p className="text-xs font-semibold text-purple-700 uppercase tracking-wider">Criterios evaluados</p>
                      </div>
                      <div className="divide-y divide-gray-100">
                        {b7Review.criteria.map((c, idx) => (
                          <div key={idx} className="px-4 py-3 flex items-center justify-between gap-4">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-800">{c.name}</p>
                              {c.description && <p className="text-xs text-gray-500 mt-0.5">{c.description}</p>}
                              <p className="text-xs text-gray-400 mt-0.5">Peso: {c.weight}%</p>
                            </div>
                            <div className="shrink-0 flex items-center gap-1">
                              <span className={`text-lg font-bold ${c.score >= 7 ? 'text-green-600' : c.score >= 5 ? 'text-yellow-600' : 'text-red-500'}`}>
                                {c.score}
                              </span>
                              <span className="text-xs text-gray-400">/ 10</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Notas del docente */}
                  {b7Review.instructorNotes && (
                    <div className="bg-white border border-purple-100 rounded-xl p-4">
                      <p className="text-xs font-semibold text-purple-700 uppercase tracking-wider mb-2">Notas del docente</p>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{b7Review.instructorNotes}</p>
                    </div>
                  )}

                  {/* Fecha de revisión */}
                  {b7Review.reviewedAt && (
                    <p className="text-xs text-gray-400 text-right">
                      Revisado el {new Date(b7Review.reviewedAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

        </div>{/* fin área de contenido */}
      </div>{/* fin cuerpo */}
    </div>
  );
};

export default StudentWeekExecutionPage;
